import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProjetcsService } from 'src/app/projects/projetcs.service';
import { GlobalService } from './global.service';
import { XlsExportService } from './xls-export.service';

export interface IPendingFile {
	status: string, // pending, complete, failed
	taskId: number,
	fileName: string,
	action: string, // view - download
	info?: any,
	isFrontReport?: boolean
};

@Injectable({
	providedIn: 'root'
})
export class BackgroundFilesService {

	private pendingFilesSource: BehaviorSubject<Array<IPendingFile>> = new BehaviorSubject([]);
	public pendingFiles$: Observable<any> = this.pendingFilesSource.asObservable();
	public lastEventIsRemove: boolean;

	addPendingFile(pendingFile: IPendingFile) {
		let current: Array<any> = this.pendingFilesSource.value;
		current.push(pendingFile);
		this.lastEventIsRemove = false;
		this.pendingFilesSource.next(current);
	}

	changeStatusFile(pendingFile: IPendingFile, status: string) {
		let current: Array<any> = this.pendingFilesSource.value;
		let i = current.findIndex(f => f.taskId == pendingFile.taskId);
		current[i].status = status;
		this.lastEventIsRemove = false;
		this.pendingFilesSource.next(current);
	}

	removeFile(pendingFile: IPendingFile) {
		let current: Array<any> = this.pendingFilesSource.value;
		let i = current.findIndex(f => f.taskId == pendingFile.taskId);
		current.splice(i, 1);
		this.lastEventIsRemove = true;
		this.pendingFilesSource.next(current);
	}

	getStatusPendingList() {
		let current: Array<IPendingFile> = this.pendingFilesSource.value;
		for (const file of current) {
			if (file.status == 'pending') {
				return 'pending';
			}
		}
		return 'complete';
	}
	/**
	 * Función para saber si un elemento especifico tiene archivos pendientes
	 * @param value 
	 * @param property 
	 */
	getStatusPendingListByInfo(value, property) {
		let current: Array<IPendingFile> = this.pendingFilesSource.value.filter(f => f.info[property] == value);
		for (const file of current) {
			if (file.status == 'pending') {
				return 'pending';
			}
		}
		return 'finished';
	}

	/**
	 * =========================================
	 * Custom functions to use in the background.
	 * =========================================
	 */

	/**
	 * Adds images to brand
	 */
	sendImagesBrand(selectedFiles, values, selectedOriginalDesign) {
		let i = 0;
		for (const file of selectedFiles) {
			let pendingFile: IPendingFile = {
				'fileName': file.name,
				'action': 'view',
				'taskId': Date.now() + Math.random(),
				'status': 'pending',
				'info': {
					'brand_id': values.element_id
				}
			};
			this.addPendingFile(pendingFile);
			let isOriginalDesign = selectedOriginalDesign[i];
			this._pS.addImageToBrand(file, values.scene_id, values.element_id, isOriginalDesign).subscribe(
				data => {
					this.changeStatusFile(pendingFile, 'complete');
				},
				err => {
					console.log("Error uploading file", err);
					this.changeStatusFile(pendingFile, 'failed');
				}
			)
			i++;
		}
	}

	/**
	 * Adds an excel report already generated to pending list
	 * @param pendingFile 
	 */
	addReportExcelFront(pendingFile: IPendingFile) {
		this.addPendingFile(pendingFile);
		setTimeout(() => {
			this.changeStatusFile(pendingFile, 'complete');
		}, 1500)
	}

	reportLocationsInChapters(project, ids) {
		let report_name = project.title;
		report_name += this._gS.getLenguage() == 'esp' ? '_locaciones_en_capitulo' : '_locations_in_chapter';

		let pendingFile: IPendingFile = {
			'fileName': report_name + '_export_' + new Date().getTime() + '.xlsx',
			'action': 'download',
			'taskId': Date.now() + Math.random(),
			'status': 'pending',
			'isFrontReport': true,
		};

		this.addPendingFile(pendingFile);

		this._pS.locationsInChapters(project.id, ids)
			.subscribe(
				data => {
					let rdata: any = data;
					let tempPendingFile: IPendingFile;
					if (this._gS.getLenguage() == 'esp') {
						tempPendingFile = this.xlsService.exportCsv(rdata.locations, rdata.keys_esp, report_name, rdata.label_esp);
					} else {
						tempPendingFile = this.xlsService.exportCsv(rdata.locations, rdata.keys_eng, report_name, rdata.label_eng);
					}
					pendingFile.info = tempPendingFile.info;
					this.changeStatusFile(pendingFile, 'complete');
				},
				err => {
					this.changeStatusFile(pendingFile, 'failed');
				}
			)
	}

	reportSetsInChapters(project, ids) {
		let report_name = project.title;
		report_name += this._gS.getLenguage() == 'esp' ? '_sets_en_capitulo' : '_sets_in_chapter';

		let pendingFile: IPendingFile = {
			'fileName': report_name + '_export_' + new Date().getTime() + '.xlsx',
			'action': 'download',
			'taskId': Date.now() + Math.random(),
			'status': 'pending',
			'isFrontReport': true,
		};

		this.addPendingFile(pendingFile);

		this._pS.setsInChapters(project.id, ids)
			.subscribe(
				data => {
					let rdata: any = data;
					let tempPendingFile: IPendingFile;
					if (this._gS.getLenguage() == 'esp') {
						tempPendingFile = this.xlsService.exportCsv(rdata.sets, rdata.keys_esp, report_name, rdata.label_esp);
					} else {
						tempPendingFile = this.xlsService.exportCsv(rdata.sets, rdata.keys_eng, report_name, rdata.label_eng);
					}
					pendingFile.info = tempPendingFile.info;
					this.changeStatusFile(pendingFile, 'complete');
				},
				err => {
					this.changeStatusFile(pendingFile, 'failed');
				}
			)
	}

	reportCharactersInChapters(project, ids, filter = null) {
		let report_name = project.title;
		report_name += this._gS.getLenguage() == 'esp' ? '_personajes_en_capitulo' : '_characters_in_chapter';

		let pendingFile: IPendingFile = {
			'fileName': report_name + '_export_' + new Date().getTime() + '.xlsx',
			'action': 'download',
			'taskId': Date.now() + Math.random(),
			'status': 'pending',
			'isFrontReport': true,
		};

		this.addPendingFile(pendingFile);

		this._pS.charactersInChapters(project.id, ids, filter)
			.subscribe(
				data => {
					let rdata: any = data;
					let tempPendingFile: IPendingFile;
					if (this._gS.getLenguage() == 'esp') {
						tempPendingFile = this.xlsService.exportCsv(rdata.characters, rdata.keys_esp, report_name, rdata.label_esp);
					} else {
						tempPendingFile = this.xlsService.exportCsv(rdata.characters, rdata.keys_eng, report_name, rdata.label_eng);
					}
					pendingFile.info = tempPendingFile.info;
					this.changeStatusFile(pendingFile, 'complete');
				},
				err => {
					this.changeStatusFile(pendingFile, 'failed');
				}
			)
	}

	reportElementsInChapters(project, type) {
		let report_name = project.title;
		report_name += this._gS.getLenguage() == 'esp' ? '_elementos_en_capitulo' : '_elements_in_chapter';

		let pendingFile: IPendingFile = {
			'fileName': report_name + '_export_' + new Date().getTime() + '.xlsx',
			'action': 'download',
			'taskId': Date.now() + Math.random(),
			'status': 'pending',
			'isFrontReport': true,
		};

		this.addPendingFile(pendingFile);

		this._pS.elementsInChapters(project.id, type)
			.subscribe(
				data => {
					let rdata: any = data;
					let tempPendingFile: IPendingFile;
					if (this._gS.getLenguage() == 'esp') {
						tempPendingFile = this.xlsService.exportCsv(rdata.elements, rdata.keys_esp, report_name, rdata.label_esp);
					} else {
						tempPendingFile = this.xlsService.exportCsv(rdata.elements, rdata.keys_eng, report_name, rdata.label_eng);
					}
					pendingFile.info = tempPendingFile.info;
					this.changeStatusFile(pendingFile, 'complete');
				},
				err => {
					this.changeStatusFile(pendingFile, 'failed');
				}
			)
	}


	constructor(private _pS: ProjetcsService, private xlsService: XlsExportService, private _gS: GlobalService) { }
}
